import React, { useState, useEffect } from 'react'
import DateFormat from '../../shared/helpers/date-format'
import { Loading } from '../../shared/components'
import { safeApiCall } from '../../services/api'
import storage from '../../services/storage'
import classNames from 'classnames'
import { getTotalTicketCost } from '../helpers/exchange-data'
import { clearTicketsAppSessionData } from '../../shared/helpers/logic'
import { formatValueAsCurrency } from '../../shared/helpers/data-display-format'

const OrderExchangeView = ({
  order,
  exchangeItem,
  isMember,
  isCancelRequest
}) => {
  document.body.scrollTop = document.documentElement.scrollTop = 0
  let { slashed, time } = DateFormat

  const orderNumber = order.odOrderNumber

  const [chosenExchangeItems, setChosenExchangeItems] = useState([])

  const [promptPerformances, setPromptPerformances] = useState([])

  const [isLoading, setIsLoading] = useState(false)

  const [isError, setIsError] = useState(false)

  const [isInCancelMode, setIsInCancelMode] = useState(isCancelRequest)

  const [totalExchangeBalance, setTotalExchangeBalance] = useState(0.0)

  useEffect(() => {
    const baseEvent = buildItemForExchange(exchangeItem)

    let itemsToExchange = [baseEvent]

    exchangeItem.itemsAutomaticallyIncludedInExchange.forEach((item, index) => {
      const addOn = buildItemForExchange(item, true)
      itemsToExchange.push(addOn)
    })

    let promptForPerformanceIds = exchangeItem.itemsToPromptForExchange.map(
      ei => ei.exchangeData.performanceId
    )
    setPromptPerformances(promptForPerformanceIds)

    setChosenExchangeItems(itemsToExchange)
  }, [])

  useEffect(() => {
    computeTotalExchangeCost(chosenExchangeItems)
  }, [chosenExchangeItems])

  const computeTotalExchangeCost = exchangeItems => {
    // This should be a flat array of items to exchange.
    let totalCost = 0
    exchangeItems.forEach((item, index) => {
      totalCost += getItemExchangeCost(item.ticketInfo)
    })

    setTotalExchangeBalance(totalCost)
    if (totalCost == 0) {
      setIsInCancelMode(true)
    } else {
      setIsInCancelMode(false)
    }
  }

  const buildItemForExchange = (data, isAddOn) => {
    const exchangeableItem = {
      nativeEventId: isAddOn
        ? data.exchangeData.addOnNativeEventId
        : data.exchangeData.nativeEventId,
      eventType: isAddOn ? 'addon' : data.exchangeData.eventType,
      performanceId: data.exchangeData.performanceId,
      performanceDate: data.exchangeData.perfDate,
      title: data.exchangeData.title,
      ticketInfo: data.exchangeData.ticketInfo
    }

    return exchangeableItem
  }

  const onClickExchange = async e => {
    e.preventDefault()
    setIsLoading(true)

    const body = {
      orderNumber: orderNumber,
      itemsForExchange: chosenExchangeItems, // was itemsToExchange
      returnOnly: isInCancelMode
    }

    let errorMessage = 'There was a problem initiating the exchange'

    try {
      const result = await safeApiCall('post', '/exchanges', errorMessage, body)
      if (result.status == 200) {
        clearTicketsAppSessionData()

        // If we are not cancelling the order, then go to the tickets path
        if (!isInCancelMode) {
          window.location.href = `/tickets?exchange=${result.exchangeId}`
        } else {
          // The order was cancelled so go back to the order history screen which someday will
          // make sense ;)
          window.location.href = `/order_history/${orderNumber}`
        }
      } else {
        setIsLoading(false)
        setIsError(true)
      }
    } catch (e) {
      setIsLoading(false)
      setIsError(true)
    }
  }

  const getItemExchangeCost = tickets => {
    // Get the number of adult tickets the user is trying to purchase for this specific add on
    let totalCost = getTotalTicketCost(tickets)
    return totalCost
  }

  const removeFromPromptPerformances = perfId => {
    setPromptPerformances(promptPerformances.filter(pp => pp != perfId))
  }

  const includeAddOnChange = event => {
    const prompItem = exchangeItem.itemsToPromptForExchange.find(
      a => a.exchangeData.performanceId == event.target.value
    )
    const itemToAdd = buildItemForExchange(prompItem)

    setChosenExchangeItems([...chosenExchangeItems, itemToAdd])

    removeFromPromptPerformances(event.target.value)
  }

  const excludeAddOnChange = event => {
    setChosenExchangeItems(
      chosenExchangeItems.filter(a => a.performanceId != event.target.value)
    )

    removeFromPromptPerformances(event.target.value)
  }

  const disableButton = promptPerformances.length > 0 || isLoading

  return (
    <>
      <div>
        {isLoading && (
          <Loading
            text={isInCancelMode ? 'Cancelling Tickets' : 'Starting Exchange'}
          />
        )}
      </div>
      {!isLoading && isError && (
        <strong>
          We're having trouble completing this request. Please call us at
          312-939-2438 or email{' '}
          <a href="mailto:contactus@sheddaquarium.org">
            contactus@sheddaquarium.org
          </a>{' '}
          for assistance.
        </strong>
      )}
      {!isLoading && (
        <div className="bordered-content">
          <h3 className="order-history-header">
            {isInCancelMode
              ? 'You are cancelling:'
              : 'You are exchanging:'}{' '}
          </h3>

          <div key={exchangeItem.exchangeData.performanceId}>
            <p className="pb-0 mb-0 mt-32">
              <strong>{exchangeItem.exchangeData.title}</strong>
              <br />

              {slashed(exchangeItem.exchangeData.perfDate)}

              {exchangeItem.exchangeData.hasZones
                ? [', ', exchangeItem.exchangeData.zoneDisplayName]
                : null}
            </p>
            {exchangeItem.tickets.map((ticket, i) => (
              <span key={ticket.priceTypeId}>
                {ticket.priceTypeLabel !== null
                  ? ticket.ticketQnty
                  : `Quantity: ${ticket.ticketQnty}`}{' '}
                {ticket.priceTypeLabel}
                {i < exchangeItem.tickets.length - 1 ? ', ' : ' '}
              </span>
            ))}

            <div
              data-testid={`exchangeBalance_${exchangeItem.exchangeData.performanceId}`}
            >
              Exchange Balance:{' '}
              {formatValueAsCurrency(getItemExchangeCost(exchangeItem.tickets))}
            </div>

            {exchangeItem.itemsAutomaticallyIncludedInExchange.map(
              (addOn, i) => (
                <div
                  key={i}
                  data-testid={`addOn_${addOn.exchangeData.performanceId}`}
                >
                  <p className="pb-0 mb-0 mt-40">
                    <strong>{addOn.exchangeData.title}</strong>
                    <br />
                    {slashed(addOn.exchangeData.perfDate)}
                    {addOn.exchangeData.hasZones
                      ? [', ', addOn.exchangeData.zoneDisplayName]
                      : null}
                    {addOn.exchangeData.isTimed
                      ? [', ', time(addOn.exchangeData.perfDateTime)]
                      : null}
                  </p>
                  {addOn.tickets.map((ticket, i) => (
                    <span key={ticket.priceTypeId}>
                      {ticket.priceTypeLabel !== null
                        ? ticket.ticketQnty
                        : `Quantity: ${ticket.ticketQnty}`}{' '}
                      {ticket.priceTypeLabel}
                      {i < addOn.tickets.length - 1 ? ', ' : ' '}
                    </span>
                  ))}

                  <div
                    data-testid={`exchangeBalance_${addOn.exchangeData.performanceId}`}
                  >
                    Exchange Balance:{' '}
                    {formatValueAsCurrency(getItemExchangeCost(addOn.tickets))}
                  </div>
                </div>
              )
            )}

            <div>
              {exchangeItem.itemsToPromptForExchange.map((addOn, i) => (
                <div
                  key={i}
                  className="add-on-prompt-box"
                  data-testid={`addOn_${addOn.exchangeData.performanceId}`}
                >
                  <p className="pb-0 text-depths font-bold">
                    Would you like to include your {addOn.exchangeData.title} in
                    this exchange?
                  </p>
                  <p className="text-dark-red leading-normal">
                    A response is required to continue.
                  </p>
                  <p className="pb-0 pt-20">
                    <strong>{addOn.exchangeData.title}</strong>
                    <br />
                    {slashed(addOn.exchangeData.perfDate)}
                    {addOn.exchangeData.hasZones
                      ? [', ', addOn.exchangeData.zoneDisplayName]
                      : null}
                    {addOn.exchangeData.isTimed
                      ? [', ', time(addOn.exchangeData.perfDateTime)]
                      : null}
                  </p>
                  <div>
                    {addOn.tickets.map((ticket, i) => (
                      <span key={ticket.priceTypeId}>
                        {ticket.priceTypeLabel !== null
                          ? ticket.ticketQnty
                          : `Quantity: ${ticket.ticketQnty}`}{' '}
                        {ticket.priceTypeLabel}
                        {i < addOn.tickets.length - 1 ? ', ' : ' '}
                      </span>
                    ))}
                  </div>
                  <div
                    data-testid={`exchangeBalance_${addOn.exchangeData.performanceId}`}
                  >
                    Exchange Balance:{' '}
                    {formatValueAsCurrency(getItemExchangeCost(addOn.tickets))}
                  </div>
                  <div className="pt-20">
                    <div>
                      <input
                        id={`Y_${addOn.exchangeData.performanceId}`}
                        type={'radio'}
                        value={addOn.exchangeData.performanceId}
                        data-value={addOn.exchangeData.performanceId}
                        name={'chooseAddOn'}
                        onChange={includeAddOnChange}
                      />
                      <label htmlFor={`Y_${addOn.exchangeData.performanceId}`}>
                        {`Yes I would like to change the date of my ${addOn.exchangeData.title}.`}
                      </label>
                    </div>
                    <div className="pt-8">
                      <input
                        id={`N_${addOn.exchangeData.performanceId}`}
                        type={'radio'}
                        value={addOn.exchangeData.performanceId}
                        data-value={addOn.exchangeData.performanceId}
                        name={'chooseAddOn'}
                        onChange={excludeAddOnChange}
                      />
                      <label htmlFor={`N_${addOn.exchangeData.performanceId}`}>
                        {`No I will attend my ${
                          addOn.exchangeData.title
                        } on ${slashed(addOn.exchangeData.perfDate)}.`}
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {isInCancelMode && (
            <div className="yellow-bordered-content">
              <p
                data-testid={'totalExchangeBalance'}
                className="total-exchange-balance"
              >
                {isInCancelMode && (
                  <>
                    <strong>
                      This {formatValueAsCurrency(totalExchangeBalance)} order
                      will be canceled and no refund will be issued.
                    </strong>{' '}
                    <span>
                      This action is irreversible. Would you like to
                      continue?{' '}
                    </span>
                  </>
                )}
              </p>
              {
                <button
                  onClick={e => onClickExchange(e)}
                  className={classNames('button -dark -medium mt-12', {
                    '-disabled': disableButton,
                    'cursor-not-allowed': disableButton
                  })}
                  disabled={disableButton}
                >
                  Cancel These Tickets
                </button>
              }
            </div>
          )}
          {!isInCancelMode && (
            <div className="yellow-bordered-content">
              {!isMember && (
                <p className="convenience-fee-notice">
                  You will be charged a convenience fee of{' '}
                  {formatValueAsCurrency(exchangeItem.exchangeFee)}.
                </p>
              )}
              <p
                data-testid={'totalExchangeBalance'}
                className="total-exchange-balance"
              >
                <strong>
                  Your Total Exchange Balance:{' '}
                  {formatValueAsCurrency(totalExchangeBalance)}
                </strong>
              </p>
              {
                <button
                  onClick={e => onClickExchange(e)}
                  className={classNames('button -dark -medium mt-16', {
                    '-disabled': disableButton,
                    'cursor-not-allowed': disableButton
                  })}
                  disabled={disableButton}
                >
                  Begin Exchange
                </button>
              }
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default OrderExchangeView
